import { z } from 'zod';
import domainEntitlementPolicy from '../../../../config/domain-router/ystnj-domain-entitlement-policy.json';
import {
  parseCanonicalOrMigrationCognitoGroup,
  normalizeCanonicalOrMigrationCognitoGroups,
} from './ystnjCognitoGroupVocabularyCompat';

const ACCESS_LANES = [
  'parent',
  'staff',
  'admin',
  'super-administrator',
] as const;

const ENTITLEMENT_IDS = [
  'module.assistant',
  'module.admissions',
  'module.financial-aid',
  'module.attendance',
  'module.academics',
  'module.tuition',
  'module.students-families',
  'module.faculty',
  'module.finance',
  'module.donations',
  'module.camps',
  'module.substitutes',
  'module.payroll',
  'module.settings',
  'module.control-plane',
  'data.contact.read',
  'data.medical.read',
  'data.financial.read',
  'notes.write',
  'documents.manage',
  'schedule.manage',
  'communications.send',
  'status.write',
  'assign.write',
  'reports.export',
  'reports.analytics',
  'admissions.review',
  'admissions.decide',
  'financial-aid.review',
  'financial-aid.decide',
  'tuition.manage',
  'payroll.admin',
  'settings.manage',
  'settings.staff.manage',
  'settings.permissions.manage',
  'settings.integrations.manage',
  'identity.view-as',
  'identity.impersonate',
  'identity.school-context.switch',
  'control-plane.manage',
] as const;

const STAFF_ROLE_TEMPLATES = [
  'staff-general',
  'staff-readonly',
  'teacher-limited',
  'principal',
  'nurse',
  'accounts',
  'admissions-reviewer',
  'financial-aid-reviewer',
  'administrator',
  'super-administrator',
] as const;

const MODULE_IDS = [
  'assistant',
  'admissions',
  'financial-aid',
  'attendance',
  'academics',
  'tuition',
  'students-families',
  'faculty',
  'finance',
  'donations',
  'camps',
  'substitutes',
  'payroll',
  'settings',
  'control-plane',
] as const;

export const YstnjAccessLaneSchema = z.enum(ACCESS_LANES);
export const YstnjDomainEntitlementIdSchema = z.enum(ENTITLEMENT_IDS);
export const YstnjStaffRoleTemplateSchema = z.enum(STAFF_ROLE_TEMPLATES);
export const YstnjModuleIdSchema = z.enum(MODULE_IDS);

export type YstnjAccessLane = z.infer<typeof YstnjAccessLaneSchema>;
export type YstnjDomainEntitlementId = z.infer<typeof YstnjDomainEntitlementIdSchema>;
export type YstnjStaffRoleTemplate = z.infer<typeof YstnjStaffRoleTemplateSchema>;
export type YstnjEntitlementModuleId = z.infer<typeof YstnjModuleIdSchema>;

const StudentAccessModeSchema = z.enum([
  'all-school',
  'division-scoped',
  'assigned-only',
]);

const FamilyAccessModeSchema = z.enum([
  'all-school',
  'linked-only',
]);

export const YstnjEntitlementScopeSchema = z.object({
  schoolIds: z.array(z.string()).default([]),
  divisionIds: z.array(z.string()).default([]),
  studentAccessMode: StudentAccessModeSchema.default('all-school'),
  familyAccessMode: FamilyAccessModeSchema.default('all-school'),
}).strict();

export type YstnjEntitlementScope = z.infer<typeof YstnjEntitlementScopeSchema>;

const RoleTemplatePolicySchema = z.object({
  accessLane: YstnjAccessLaneSchema,
  scope: YstnjEntitlementScopeSchema,
  grants: z.array(YstnjDomainEntitlementIdSchema),
  denies: z.array(YstnjDomainEntitlementIdSchema),
}).strict();

const PolicySchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  stableAccessGroups: z.array(z.string()).nonempty(),
  laneProjectionByGroup: z.record(z.string(), YstnjAccessLaneSchema),
  moduleEntitlements: z.record(z.string(), YstnjDomainEntitlementIdSchema),
  reviewEntitlementsByModule: z.record(z.string(), YstnjDomainEntitlementIdSchema),
  legacyPermissionClaims: z.record(z.string(), z.array(YstnjDomainEntitlementIdSchema)),
  templateDefaultsByGroup: z.record(z.string(), YstnjStaffRoleTemplateSchema.nullable()),
  roleTemplates: z.record(z.string(), RoleTemplatePolicySchema),
}).strict();

export const YSTNJ_DOMAIN_ENTITLEMENT_POLICY = PolicySchema.parse(domainEntitlementPolicy);

export const YSTNJ_STABLE_ACCESS_GROUPS = [
  ...YSTNJ_DOMAIN_ENTITLEMENT_POLICY.stableAccessGroups,
] as YstnjAccessLane[];

export const YSTNJ_ENTITLEMENT_CLAIM_KEYS = [
  'triedge:entitlements',
  'custom:entitlements',
  'entitlements',
  'entitlement',
] as const;

export const YSTNJ_ACCESS_LANE_CLAIM_KEYS = [
  'triedge:accessLane',
  'custom:accessLane',
  'accessLane',
] as const;

export const YSTNJ_STAFF_ROLE_TEMPLATE_CLAIM_KEYS = [
  'triedge:staffRoleTemplate',
  'custom:staffRoleTemplate',
  'staffRoleTemplate',
] as const;

export const YSTNJ_ENTITLEMENT_SCOPE_CLAIM_KEYS = [
  'triedge:entitlementScope',
  'custom:entitlementScope',
  'entitlementScope',
] as const;

export type YstnjEffectiveEntitlementSnapshot = {
  accessLane: YstnjAccessLane;
  staffRoleTemplate: YstnjStaffRoleTemplate | null;
  entitlements: YstnjDomainEntitlementId[];
  scope: YstnjEntitlementScope | null;
  source: 'claims' | 'template';
};

const normalizeToken = (value: unknown): string =>
  String(value ?? '').trim().toLowerCase();

const normalizeUnique = (values: readonly string[]): string[] =>
  [...new Set(values.map((value) => normalizeToken(value)).filter(Boolean))];

const normalizeDelimitedValues = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return normalizeUnique(value.map(String));
  }
  if (typeof value === 'string') {
    return normalizeUnique(value.split(/[,\s]+/));
  }
  return [];
};

const parseTemplate = (value: unknown): YstnjStaffRoleTemplate | null => {
  const parsed = YstnjStaffRoleTemplateSchema.safeParse(normalizeToken(value));
  return parsed.success ? parsed.data : null;
};

const parseAccessLane = (value: unknown): YstnjAccessLane | null => {
  const parsed = YstnjAccessLaneSchema.safeParse(normalizeToken(value));
  return parsed.success ? parsed.data : null;
};

const parseEntitlement = (
  value: unknown,
): YstnjDomainEntitlementId | null => {
  const parsed = YstnjDomainEntitlementIdSchema.safeParse(normalizeToken(value));
  return parsed.success ? parsed.data : null;
};

const parseEntitlementList = (value: unknown): YstnjDomainEntitlementId[] => [
  ...new Set(
    normalizeDelimitedValues(value)
      .map((entry) => parseEntitlement(entry))
      .filter(Boolean),
  ),
] as YstnjDomainEntitlementId[];

export const resolveAccessLaneFromClaims = (
  claims: Record<string, unknown> | null | undefined,
): YstnjAccessLane | null => {
  for (const key of YSTNJ_ACCESS_LANE_CLAIM_KEYS) {
    const lane = parseAccessLane(claims?.[key]);
    if (lane) return lane;
  }
  return null;
};

export const resolveStaffRoleTemplateFromClaims = (
  claims: Record<string, unknown> | null | undefined,
): YstnjStaffRoleTemplate | null => {
  for (const key of YSTNJ_STAFF_ROLE_TEMPLATE_CLAIM_KEYS) {
    const template = parseTemplate(claims?.[key]);
    if (template) return template;
  }
  return null;
};

export const resolveEntitlementClaims = (
  claims: Record<string, unknown> | null | undefined,
): YstnjDomainEntitlementId[] => {
  const values = YSTNJ_ENTITLEMENT_CLAIM_KEYS.flatMap((key) => parseEntitlementList(claims?.[key]));
  return [...new Set(values)] as YstnjDomainEntitlementId[];
};

export const resolveEntitlementScopeFromClaims = (
  claims: Record<string, unknown> | null | undefined,
): YstnjEntitlementScope | null => {
  for (const key of YSTNJ_ENTITLEMENT_SCOPE_CLAIM_KEYS) {
    const raw = claims?.[key];
    if (!raw) {
      continue;
    }
    const candidate = (() => {
      if (typeof raw === 'string') {
        try {
          return JSON.parse(raw) as unknown;
        } catch {
          return null;
        }
      }
      return raw;
    })();
    const parsed = YstnjEntitlementScopeSchema.safeParse(candidate);
    if (parsed.success) {
      return parsed.data;
    }
  }
  return null;
};

export const resolveProjectedStableAccessGroups = (
  groups: readonly unknown[] | null | undefined,
): YstnjAccessLane[] => {
  const normalizedGroups = normalizeCanonicalOrMigrationCognitoGroups(groups);
  const lanes = new Set<YstnjAccessLane>();
  for (const group of normalizedGroups) {
    const lane = parseAccessLane(
      YSTNJ_DOMAIN_ENTITLEMENT_POLICY.laneProjectionByGroup[group],
    );
    if (lane) {
      lanes.add(lane);
    }
  }
  return [...lanes];
};

export const resolveAccessLaneFromGroups = (
  groups: readonly unknown[] | null | undefined,
  role?: unknown,
): YstnjAccessLane | null => {
  const projected = resolveProjectedStableAccessGroups(groups);
  if (projected.includes('super-administrator')) return 'super-administrator';
  if (projected.includes('parent')) return 'parent';
  if (projected.includes('admin')) return 'admin';
  if (projected.includes('staff')) return 'staff';
  return parseAccessLane(role);
};

export const groupsProjectToStaffLane = (
  groups: readonly unknown[] | null | undefined,
): boolean => resolveProjectedStableAccessGroups(groups).includes('staff');

export const resolveStableCognitoGroupsForAuth = (
  groups: readonly unknown[] | null | undefined,
): string[] => {
  const normalizedGroups = normalizeCanonicalOrMigrationCognitoGroups(groups);
  const stableGroups = new Set<string>();
  for (const group of normalizedGroups) {
    if (group === 'viewer') {
      stableGroups.add(group);
      continue;
    }
    const lane = parseAccessLane(
      YSTNJ_DOMAIN_ENTITLEMENT_POLICY.laneProjectionByGroup[group],
    );
    if (lane) {
      stableGroups.add(lane);
    }
  }
  return normalizeUnique([...stableGroups]);
};

export const resolveDefaultStaffRoleTemplateFromGroups = (
  groups: readonly unknown[] | null | undefined,
): YstnjStaffRoleTemplate | null => {
  const normalizedGroups = normalizeCanonicalOrMigrationCognitoGroups(groups);
  const precedence = [
    'super-administrator',
    'admin',
    'owner',
    'administrator',
    'nurse',
    'viewer',
    'editor',
    'staff',
    'admissions-review',
    'financial-aid-review',
    'parent',
  ];
  for (const token of precedence) {
    const canonical = parseCanonicalOrMigrationCognitoGroup(token);
    if (!canonical || !normalizedGroups.includes(canonical)) {
      continue;
    }
    const template = parseTemplate(
      YSTNJ_DOMAIN_ENTITLEMENT_POLICY.templateDefaultsByGroup[canonical],
    );
    if (template) {
      return template;
    }
  }
  return null;
};

export const resolveModuleEntitlement = (
  moduleId: string | null | undefined,
): YstnjDomainEntitlementId | null => {
  const token = normalizeToken(moduleId);
  return parseEntitlement(
    YSTNJ_DOMAIN_ENTITLEMENT_POLICY.moduleEntitlements[token],
  );
};

export const resolveReviewEntitlement = (
  moduleId: string | null | undefined,
): YstnjDomainEntitlementId | null => {
  const token = normalizeToken(moduleId);
  return parseEntitlement(
    YSTNJ_DOMAIN_ENTITLEMENT_POLICY.reviewEntitlementsByModule[token],
  );
};

export const resolveTemplateEntitlements = (
  template: YstnjStaffRoleTemplate | null | undefined,
): YstnjDomainEntitlementId[] => {
  if (!template) {
    return [];
  }
  const policy = YSTNJ_DOMAIN_ENTITLEMENT_POLICY.roleTemplates[template];
  if (!policy) {
    return [];
  }
  const grants = new Set(policy.grants);
  for (const denied of policy.denies) {
    grants.delete(denied);
  }
  return [...grants];
};

export const resolveTemplateScope = (
  template: YstnjStaffRoleTemplate | null | undefined,
): YstnjEntitlementScope | null => {
  if (!template) {
    return null;
  }
  const policy = YSTNJ_DOMAIN_ENTITLEMENT_POLICY.roleTemplates[template];
  if (!policy) {
    return null;
  }
  return policy.scope;
};

export const resolveTemplateAccessLane = (
  template: YstnjStaffRoleTemplate | null | undefined,
): YstnjAccessLane | null => {
  if (!template) {
    return null;
  }
  const policy = YSTNJ_DOMAIN_ENTITLEMENT_POLICY.roleTemplates[template];
  return policy?.accessLane ?? null;
};

export const resolveEffectiveEntitlementSnapshot = (input: {
  groups?: readonly unknown[] | null;
  role?: unknown;
  accessLaneClaim?: unknown;
  staffRoleTemplateClaim?: unknown;
  entitlementClaims?: readonly unknown[] | null;
  entitlementScopeClaim?: unknown;
}): YstnjEffectiveEntitlementSnapshot | null => {
  const accessLane = parseAccessLane(input.accessLaneClaim)
    ?? resolveAccessLaneFromGroups(input.groups, input.role);
  if (!accessLane) {
    return null;
  }

  const claimTemplate = parseTemplate(input.staffRoleTemplateClaim);
  const defaultTemplate = resolveDefaultStaffRoleTemplateFromGroups(input.groups);
  const template = (() => {
    if (!claimTemplate) {
      return defaultTemplate;
    }
    return resolveTemplateAccessLane(claimTemplate) === accessLane
      ? claimTemplate
      : defaultTemplate;
  })();

  const claimEntitlements = [
    ...new Set(
      (input.entitlementClaims ?? [])
        .map((entry) => parseEntitlement(entry))
        .filter(Boolean),
    ),
  ] as YstnjDomainEntitlementId[];

  if (claimEntitlements.length > 0) {
    return {
      accessLane,
      staffRoleTemplate: template,
      entitlements: claimEntitlements,
      scope: YstnjEntitlementScopeSchema.safeParse(input.entitlementScopeClaim).success
        ? YstnjEntitlementScopeSchema.parse(input.entitlementScopeClaim)
        : resolveTemplateScope(template),
      source: 'claims',
    };
  }

  return {
    accessLane,
    staffRoleTemplate: template,
    entitlements: resolveTemplateEntitlements(template),
    scope: resolveTemplateScope(template),
    source: 'template',
  };
};

export const resolveLegacyPermissionClaimsFromEntitlements = (
  entitlements: readonly unknown[] | null | undefined,
): string[] => {
  const entitlementSet = new Set(
    (entitlements ?? [])
      .map((entry) => parseEntitlement(entry))
      .filter(Boolean),
  );

  return Object.entries(YSTNJ_DOMAIN_ENTITLEMENT_POLICY.legacyPermissionClaims)
    .filter(([, required]) => required.every((entry) => entitlementSet.has(entry)))
    .map(([claim]) => normalizeToken(claim))
    .filter(Boolean);
};

export const hasDomainEntitlement = (
  entitlements: readonly unknown[] | null | undefined,
  entitlement: string | null | undefined,
): boolean => {
  const normalizedEntitlement = parseEntitlement(entitlement);
  if (!normalizedEntitlement) {
    return false;
  }
  return new Set(
    (entitlements ?? [])
      .map((entry) => parseEntitlement(entry))
      .filter(Boolean),
  ).has(normalizedEntitlement);
};

export const hasModuleEntitlement = (
  entitlements: readonly unknown[] | null | undefined,
  moduleId: string | null | undefined,
): boolean => {
  const moduleEntitlement = resolveModuleEntitlement(moduleId);
  return moduleEntitlement
    ? hasDomainEntitlement(entitlements, moduleEntitlement)
    : false;
};
